<template>
  <AppRoot>
    <App>
      <NavBar>
        <router-link to="/">
          <span class="logo-block">
            <img class="logo" :src="require(`@/assets/svg/icon-brain.svg`)" />
            {{ $t('exam_portal_page_title_nonbranded') }}
          </span>
        </router-link>
        <template #account>
          <Menu
            v-if="isLoggedIn"
            offset-y
            v-model="menu"
            :close-on-click="true"
            :close-on-content-click="true"
          >
            <template v-slot:activator="{ on, attrs }">
              <b v-bind="attrs" v-on="on">
                <Icon icon="mdi-account-circle-outline" />
                {{ $t('account_link') }}
                <Icon v-if="menu" icon="mdi-chevron-up" />
                <Icon v-else icon="mdi-chevron-down" />
              </b>
            </template>

            <List dark>
              <ListItem to="/profile">{{ $t('profile_link') }}</ListItem>
              <ListItem @click="handleLogout(false)">
                {{ $t('button_sign_out') }}
              </ListItem>
            </List>
            <v-divider />
            <List dark subheader>
              <v-subheader v-html="$t('help')"/>
              <ListItem to="/faqs">{{ $t('faqs_link') }}</ListItem>
              <ListItem @click="showContactModal = true">
                {{ $t('contact_support_header') }}
              </ListItem>
              <ListItem to="/privacy">
                <p class="capitalize nowrap">
                  {{ $t('register_body_pp_link') }}
                </p>
              </ListItem>
              <ListItem to="/terms">
                <p class="capitalize">
                  {{ $t('register_body_tos_link') }}
                </p>
              </ListItem>
            </List>
          </Menu>
          <b v-else>
            <router-link to="/login">{{ $t('button_sign_in') }}</router-link>
          </b>
        </template>
      </NavBar>
      <transition name="fade" mode="out-in">
        <router-view />
      </transition>
      <Modal title="" />
      <SizeError />
      <SharedModal
        :value="showCookieModal"
        :title="$t('error_cookies_disabled_header')"
      >
        {{ $t('error_cookies_disabled_body') }}

        <template #actions>
          <Button type="primary" @click="reloadPage">
            {{ $t('button_try_again') }}
          </Button>
        </template>
      </SharedModal>
      <SharedModal
        :value="showContactModal"
        :title="$t('contact_support_header')"
        :center="false"
        @click:outside="showContactModal = false"
        @keydown.esc="showContactModal = false"
      >
        <p v-html="$t('contact_support_body')" />
        <h5 v-html="$t('email_address')" />
        <p>support@neurotrack.com</p>
        <h5 v-html="$t('mailing_address')" />
        <p>
          Neurotrack Technologies<br />
          39 Bradford St, Ste 101<br />
          Redwood City, CA 94063
        </p>
        <template #actions>
          <Button type="primary" @click="showContactModal = false">
            {{ $t('button_close') }}
          </Button>
        </template>
      </SharedModal>
    </App>
  </AppRoot>
</template>
<script>
import {
  AppRoot,
  App,
  Button,
  NavBar,
  Menu,
  List,
  ListItem,
  Icon,
  Modal as SharedModal
} from '@neurotrack/web-shared-components'
import { mapGetters, mapActions, mapState } from 'vuex'
import { ActivityMonitor, cookies, setHttpBaseUrl } from '@neurotrack/web-core'
import Modal from '@/components/modal/Modal.vue'
import SizeError from '@/SizeError.vue'

export default {
  name: 'AppContainer',
  data() {
    return {
      menu: false,
      activityMonitor: undefined,
      showContactModal: false
    }
  },
  components: {
    Modal,
    SharedModal,
    Button,
    AppRoot,
    App,
    NavBar,
    Menu,
    List,
    ListItem,
    Icon,
    SizeError
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapState('video', ['accessToCamera']),
    showCookieModal() {
      return !navigator.cookieEnabled
    }
  },
  methods: {
    ...mapActions('user', ['logout']),
    ...mapActions('video', ['checkAccess', 'checkMediaRecorder']),
    handleLogout(inactivityLogout) {
      this.menu = false
      this.logout({ inactivityLogout })
    },
    reloadPage() {
      window.location.reload()
    },
    postMessageLogin(msg) {
      if (this.isOriginAllowed(msg)) {
        if (
          msg.data.api_url
          && msg.data.content_root
          && msg.data.access_token
          && msg.data.refresh_token
          && msg.data.profile
        ) {
          this.$store.commit('user/LOGIN')
          this.$store.commit('user/SET_PROFILE', msg.data.profile)
          cookies.setCookie('api_url', msg.data.api_url, 1)
          cookies.setCookie('content_root', msg.data.content_root, 1)
          window.localStorage.setItem('access_token', msg.data.access_token)
          window.localStorage.setItem('refresh_token', msg.data.refresh_token)
          this.$store.commit('user/LOGIN_COMPLETE')
        } else {
          window.localStorage.clear()
          cookies.eraseCookie('access_token')
          cookies.eraseCookie('refresh_token')
          cookies.eraseCookie('api_url')
          cookies.eraseCookie('content_root')
        }

        msg.source.postMessage({ type: 'legacylogin' }, msg.origin)
      }
    },
    isOriginAllowed(msg) {
      return (
        (
          msg.origin === 'https://dev.exams.neurotrack.io'
          || msg.origin === 'https://exams.neurotrack.io'
          || (process.env.VUE_APP_STAGE === 'local' && msg.origin === 'https://localhost:8088')
        )
        && msg.data.type === 'legacylogin'
      )
    }
  },
  created() {
    this.checkAccess()
    this.checkMediaRecorder()
    this.activityMonitor = new ActivityMonitor.ActivityMonitor()

    this.activityMonitor.addTimeoutHandler(() => {
      if (this.isLoggedIn) {
        this.handleLogout(true)
      }
    })

    // listen for postMessage 'login'
    window.addEventListener('message', this.postMessageLogin)

    if (this.isLoggedIn) {
      setHttpBaseUrl()
    }
  },
  beforeDestroy() {
    this.activityMonitor.destroy()
    // listen for postMessage 'login'
    window.removeEventListener('message', this.postMessageLogin)
  }
}
</script>

<style scoped>
.logo-block {
  display: flex;
  align-items: center;
  font-weight: bold;
}
a .logo-block {
  color: white;
}
.logo {
  width: auto;
  height: 2rem;
  margin-right: 0.5rem;
}

.v-list-item p.capitalize::first-letter {
  text-transform: capitalize;
}

.nowrap {
  white-space: nowrap;
}
</style>
